import { useTranslation } from "react-i18next";

const en = {
  title: "Inventory",
  intro:
    "Private physical inventory. Lookups and printing never record receipts.",
  labels: "Labels",
  boxes: "Boxes",
  scan: "Scan / receive",
  stock: "Stock by product",
  counts: "Counts / reconciliation",
  orderSerials: "Order serials",
  audit: "Audit",
  loading: "Loading…",
  retry: "Refresh",
  empty: "No records",
  denied: "You are not authorized for this operation.",
  unavailable:
    "Inventory is disabled or unavailable. No changes were confirmed. Refresh before continuing.",
  conflict:
    "The record changed or the request conflicts. Refresh and review again.",
  error: "Request failed. No changes were confirmed.",
  signIn: "Sign in with an authorized staff account.",
  readOnly: "Read-only access",
  quantity: "Quantity",
  location: "Location",
  condition: "Condition",
  product: "Product",
  search: "Search products",
  searchHint: "Name, product ID, EAN, model or brand",
  notStockable: "Not stockable",
  noMatches: "No matching products",
  select: "Select…",
  review: "Review",
  confirm: "Confirm",
  cancel: "Cancel",
  version: "Version",
  reason: "Reason",
  saved: "Saved. Server record refreshed.",
  labelHelp:
    "Issue 1–500 opaque Code 128 labels. Issuing and printing labels creates no stock.",
  batches: "Previous batches",
  pdf: "Open PDF",
  reprint: "Reprint PDF (same identities)",
  printHelp:
    "Print at 100% / actual size; disable fit-to-page. Check one label with a scanner. Opening a PDF does not confirm physical printing.",
  rows: "Rows",
  columns: "Columns",
  margin: "Margin (pt)",
  module: "Barcode module (pt)",
  boxMode: "BOX — internal box code",
  unitMode: "UNIT — manufacturer serial",
  code: "Scanned or manual code",
  lookup: "Look up",
  scanHelp:
    "USB scanner: focus this field and press Enter. Lookup only; a receipt always requires Review and Confirm.",
  unknownBox: "Unknown box. No stock was changed.",
  voided: "Voided label — cannot receive stock.",
  alreadyReceived:
    "Already received. Rescanning is a read-only lookup, not another receipt.",
  duplicateScan: "Repeated scan — no new receipt.",
  unitHelp:
    "UNIT mode adds a serial to the selected intake draft only; it never looks up a box or records stock.",
  chooseBox:
    "Look up a box and select a product before capturing unit serials.",
  serials: "Manufacturer serials (one per line)",
  serialHelp:
    "Keep manufacturer text exactly. Empty lines are ignored. The box code and EAN are not serial numbers.",
  duplicateSerial:
    "Duplicate serial detected. Correct the entries before confirmation.",
  wrongSerial: "A box code or product EAN cannot be a unit serial.",
  progress: "of",
  serialPolicy: "Serial policy",
  policyRequired: "Required",
  policyOptional: "Optional",
  policyNone: "None",
  policyUnconfigured: "Not configured",
  policyWarning:
    "Policy not approved: intake stays quarantined and is not available.",
  serialWarning:
    "Required serials incomplete: stock stays quarantined. Explicit inspection holds require supervisor release.",
  policyApproval: "Approve serial policy",
  policyReason: "Policy approval reason",
  policyNote:
    "Approving a policy quarantines all existing stock for this product. A supervisor must verify and release each box.",
  receive: "Receive box",
  completeSerials: "Complete pending serials",
  move: "Move box",
  quarantine: "Quarantine box",
  voidLabel: "Void unused label",
  receiptReview: "Review receipt",
  pendingReview: "Review serial completion",
  mutationReview: "Review change",
  status: "Status",
  all: "All",
  history: "Box history",
  previous: "Previous",
  next: "Next",
  total: "Total",
  onHand: "On hand",
  quarantined: "Quarantined",
  reserved: "Reserved",
  available: "Available",
  coverage: "Coverage",
  unknownCoverage:
    "UNKNOWN coverage — these quantities do not establish complete stock availability.",
  good: "Good",
  damaged: "Damaged",
  returned: "Returned",
  unknownCondition: "Unknown",
  quarantineCondition: "Quarantine",
  unassigned: "Unassigned",
  assigned: "Assigned",
  pendingPolicy: "Quarantine — policy pending",
  pendingSerials: "Quarantine — serials pending",
  statusVoided: "Voided",
  statusQuarantined: "Quarantined",
  actor: "Actor",
  event: "Event",
  time: "Time",
  details: "Details",
  camera: "Start camera",
  stopCamera: "Stop camera",
  cameraHelp:
    "On-device decoding only. No camera frames or image files are uploaded.",
  cameraSecure:
    "Camera requires HTTPS or localhost. Use USB or manual input instead.",
  cameraFailed:
    "Camera unavailable or permission denied. Use USB, manual input or a local barcode image.",
  decodeFailed: "No readable barcode found. Try another image or manual input.",
  localImage: "Decode local barcode image",
  invoiceSerialLabel: "Serial number:",
  unitHtva: "Unit HTVA",
  totalHtva: "Total HTVA",
  netHtva: "Net HTVA",
  totalTvac: "Total TVAC",
  invoiceSerialMissing: "Not recorded",
  recordedSerials: "Previously recorded serials (read-only)",
};
export type InventoryText = { [K in keyof typeof en]: string };
const nl: InventoryText = {
  title: "Voorraad",
  intro:
    "Private fysieke voorraad. Opzoeken en afdrukken registreren nooit ontvangsten.",
  labels: "Etiketten",
  boxes: "Dozen",
  scan: "Scannen / ontvangen",
  stock: "Voorraad per product",
  counts: "Tellingen / afstemming",
  orderSerials: "Serienummers per order",
  audit: "Audit",
  loading: "Laden…",
  retry: "Vernieuwen",
  empty: "Geen gegevens",
  denied: "U bent niet bevoegd voor deze bewerking.",
  unavailable:
    "Voorraadbeheer is uitgeschakeld of niet beschikbaar. Geen wijzigingen bevestigd. Vernieuw voordat u doorgaat.",
  conflict:
    "Het record is gewijzigd of de aanvraag conflicteert. Vernieuw en controleer opnieuw.",
  error: "Aanvraag mislukt. Geen wijzigingen bevestigd.",
  signIn: "Meld u aan met een bevoegd medewerkersaccount.",
  readOnly: "Alleen-lezen toegang",
  quantity: "Aantal",
  location: "Locatie",
  condition: "Staat",
  product: "Product",
  search: "Producten zoeken",
  searchHint: "Naam, product-ID, EAN, model of merk",
  notStockable: "Geen voorraadartikel",
  noMatches: "Geen overeenkomende producten",
  select: "Selecteer…",
  review: "Controleren",
  confirm: "Bevestigen",
  cancel: "Annuleren",
  version: "Versie",
  reason: "Reden",
  saved: "Opgeslagen. Serverrecord vernieuwd.",
  labelHelp:
    "Maak 1–500 ondoorzichtige Code 128-etiketten. Aanmaken en afdrukken creëert geen voorraad.",
  batches: "Vorige batches",
  pdf: "PDF openen",
  reprint: "PDF opnieuw afdrukken (dezelfde codes)",
  printHelp:
    "Druk af op 100% / ware grootte; schakel aanpassen aan pagina uit. Controleer één etiket met een scanner. Een PDF openen bevestigt geen fysieke afdruk.",
  rows: "Rijen",
  columns: "Kolommen",
  margin: "Marge (pt)",
  module: "Barcodemodule (pt)",
  boxMode: "BOX — interne dooscode",
  unitMode: "UNIT — serienummer fabrikant",
  code: "Gescande of handmatige code",
  lookup: "Opzoeken",
  scanHelp:
    "USB-scanner: focus dit veld en druk op Enter. Alleen opzoeken; ontvangst vereist altijd Controleren en Bevestigen.",
  unknownBox: "Onbekende doos. Geen voorraad gewijzigd.",
  voided: "Ongeldig etiket — geen ontvangst mogelijk.",
  alreadyReceived:
    "Al ontvangen. Opnieuw scannen is alleen opzoeken, geen nieuwe ontvangst.",
  duplicateScan: "Herhaalde scan — geen nieuwe ontvangst.",
  unitHelp:
    "UNIT-modus voegt alleen een serienummer toe aan het gekozen ontvangstconcept; zoekt geen doos op en registreert geen voorraad.",
  chooseBox:
    "Zoek een doos op en selecteer een product voordat u serienummers vastlegt.",
  serials: "Serienummers fabrikant (één per regel)",
  serialHelp:
    "Behoud de fabrikanttekst exact. Lege regels worden genegeerd. Dooscode en EAN zijn geen serienummers.",
  duplicateSerial: "Dubbel serienummer. Corrigeer de invoer vóór bevestiging.",
  wrongSerial: "Een dooscode of product-EAN kan geen serienummer zijn.",
  progress: "van",
  serialPolicy: "Serienummerbeleid",
  policyRequired: "Verplicht",
  policyOptional: "Optioneel",
  policyNone: "Geen",
  policyUnconfigured: "Niet ingesteld",
  policyWarning:
    "Beleid niet goedgekeurd: de ontvangst blijft in quarantaine en is niet beschikbaar.",
  serialWarning:
    "Verplichte serienummers ontbreken: de voorraad blijft in quarantaine. Een voorraadbeheerder moet een expliciete inspectieblokkering vrijgeven.",
  policyApproval: "Serienummerbeleid goedkeuren",
  policyReason: "Reden beleidsgoedkeuring",
  policyNote:
    "Het goedkeuren van het beleid plaatst alle bestaande voorraad van dit product in quarantaine. Een voorraadbeheerder moet elke doos controleren en vrijgeven.",
  receive: "Doos ontvangen",
  completeSerials: "Wachtende serienummers aanvullen",
  move: "Doos verplaatsen",
  quarantine: "Doos in quarantaine",
  voidLabel: "Ongebruikt etiket ongeldig maken",
  receiptReview: "Ontvangst controleren",
  pendingReview: "Serienummers controleren",
  mutationReview: "Wijziging controleren",
  status: "Status",
  all: "Alle",
  history: "Dooshistoriek",
  previous: "Vorige",
  next: "Volgende",
  total: "Totaal",
  onHand: "Fysiek aanwezig",
  quarantined: "In quarantaine",
  reserved: "Gereserveerd",
  available: "Beschikbaar",
  coverage: "Dekking",
  unknownCoverage:
    "ONBEKENDE dekking — deze aantallen bewijzen geen volledige voorraadbeschikbaarheid.",
  good: "Goed",
  damaged: "Beschadigd",
  returned: "Retour",
  unknownCondition: "Onbekend",
  quarantineCondition: "Quarantaine",
  unassigned: "Niet toegewezen",
  assigned: "Toegewezen",
  pendingPolicy: "Quarantaine — beleid wacht",
  pendingSerials: "Quarantaine — serienummers wachten",
  statusVoided: "Ongeldig",
  statusQuarantined: "In quarantaine",
  actor: "Medewerker",
  event: "Gebeurtenis",
  time: "Tijd",
  details: "Details",
  camera: "Camera starten",
  stopCamera: "Camera stoppen",
  cameraHelp:
    "Decodering op dit apparaat. Geen camerabeelden of bestanden worden geüpload.",
  cameraSecure:
    "Camera vereist HTTPS of localhost. Gebruik USB of handmatige invoer.",
  cameraFailed:
    "Camera niet beschikbaar of toestemming geweigerd. Gebruik USB, handmatige invoer of een lokale barcodeafbeelding.",
  decodeFailed:
    "Geen leesbare barcode. Probeer een andere afbeelding of handmatige invoer.",
  localImage: "Lokale barcodeafbeelding lezen",
  invoiceSerialLabel: "Serienummer:",
  unitHtva: "Eenheid HTVA",
  totalHtva: "Totaal HTVA",
  netHtva: "Netto HTVA",
  totalTvac: "Totaal BTW",
  invoiceSerialMissing: "Niet vastgelegd",
  recordedSerials: "Eerder vastgelegde serienummers (alleen-lezen)",
};
const fr: InventoryText = {
  title: "Inventaire",
  intro:
    "Stock physique privé. La consultation et l’impression n’enregistrent jamais de réception.",
  labels: "Étiquettes",
  boxes: "Boîtes",
  scan: "Scanner / réceptionner",
  stock: "Stock par produit",
  counts: "Comptages / rapprochement",
  orderSerials: "Séries de commande",
  audit: "Audit",
  loading: "Chargement…",
  retry: "Actualiser",
  empty: "Aucun enregistrement",
  denied: "Vous n’êtes pas autorisé à effectuer cette opération.",
  unavailable:
    "Inventaire désactivé ou indisponible. Aucune modification confirmée. Actualisez avant de continuer.",
  conflict:
    "L’enregistrement a changé ou la demande est en conflit. Actualisez et vérifiez à nouveau.",
  error: "Échec de la demande. Aucune modification confirmée.",
  signIn: "Connectez-vous avec un compte employé autorisé.",
  readOnly: "Accès en lecture seule",
  quantity: "Quantité",
  location: "Emplacement",
  condition: "État",
  product: "Produit",
  search: "Rechercher des produits",
  searchHint: "Nom, identifiant, EAN, modèle ou marque",
  notStockable: "Non stockable",
  noMatches: "Aucun produit correspondant",
  select: "Sélectionner…",
  review: "Vérifier",
  confirm: "Confirmer",
  cancel: "Annuler",
  version: "Version",
  reason: "Motif",
  saved: "Enregistré. Données serveur actualisées.",
  labelHelp:
    "Créer 1–500 étiquettes Code 128 opaques. Leur création et impression ne créent aucun stock.",
  batches: "Lots précédents",
  pdf: "Ouvrir le PDF",
  reprint: "Réimprimer le PDF (mêmes identités)",
  printHelp:
    "Imprimer à 100% / taille réelle ; désactiver l’ajustement à la page. Vérifier une étiquette au scanner. Ouvrir un PDF ne confirme pas l’impression physique.",
  rows: "Lignes",
  columns: "Colonnes",
  margin: "Marge (pt)",
  module: "Module du code-barres (pt)",
  boxMode: "BOX — code interne de boîte",
  unitMode: "UNIT — numéro de série fabricant",
  code: "Code scanné ou saisi",
  lookup: "Consulter",
  scanHelp:
    "Scanner USB : placer le curseur ici puis appuyer sur Entrée. Consultation seule ; une réception exige Vérifier puis Confirmer.",
  unknownBox: "Boîte inconnue. Aucun stock modifié.",
  voided: "Étiquette annulée — réception impossible.",
  alreadyReceived:
    "Déjà réceptionnée. Un nouveau scan consulte seulement, sans nouvelle réception.",
  duplicateScan: "Scan répété — aucune nouvelle réception.",
  unitHelp:
    "Le mode UNIT ajoute une série au brouillon sélectionné seulement ; il ne consulte pas de boîte et ne crée aucun stock.",
  chooseBox:
    "Consultez une boîte et sélectionnez un produit avant de saisir les séries.",
  serials: "Numéros de série fabricant (un par ligne)",
  serialHelp:
    "Conserver le texte fabricant exact. Les lignes vides sont ignorées. Le code de boîte et l’EAN ne sont pas des séries.",
  duplicateSerial: "Numéro de série en double. Corrigez avant confirmation.",
  wrongSerial: "Un code de boîte ou un EAN produit ne peut pas être une série.",
  progress: "sur",
  serialPolicy: "Politique de série",
  policyRequired: "Obligatoire",
  policyOptional: "Facultative",
  policyNone: "Aucune",
  policyUnconfigured: "Non configurée",
  policyWarning:
    "Politique non approuvée : la réception reste en quarantaine et le stock n’est pas disponible.",
  serialWarning:
    "Numéros de série obligatoires incomplets : le stock reste en quarantaine. Une mise en quarantaine pour inspection doit être levée par un superviseur.",
  policyApproval: "Approuver la politique de série",
  policyReason: "Motif d’approbation",
  policyNote:
    "L’approbation d’une politique met tout le stock existant de ce produit en quarantaine. Un superviseur doit vérifier et libérer chaque boîte.",
  receive: "Réceptionner la boîte",
  completeSerials: "Compléter les séries en attente",
  move: "Déplacer la boîte",
  quarantine: "Mettre en quarantaine",
  voidLabel: "Annuler l’étiquette inutilisée",
  receiptReview: "Vérifier la réception",
  pendingReview: "Vérifier les séries",
  mutationReview: "Vérifier la modification",
  status: "Statut",
  all: "Tous",
  history: "Historique de boîte",
  previous: "Précédent",
  next: "Suivant",
  total: "Total",
  onHand: "Stock physique",
  quarantined: "En quarantaine",
  reserved: "Réservé",
  available: "Disponible",
  coverage: "Couverture",
  unknownCoverage:
    "Couverture INCONNUE — ces quantités ne prouvent pas une disponibilité complète du stock.",
  good: "Bon",
  damaged: "Endommagé",
  returned: "Retourné",
  unknownCondition: "Inconnu",
  quarantineCondition: "Quarantaine",
  unassigned: "Non affectée",
  assigned: "Affectée",
  pendingPolicy: "Quarantaine — politique en attente",
  pendingSerials: "Quarantaine — séries en attente",
  statusVoided: "Annulée",
  statusQuarantined: "En quarantaine",
  actor: "Auteur",
  event: "Événement",
  time: "Date",
  details: "Détails",
  camera: "Démarrer la caméra",
  stopCamera: "Arrêter la caméra",
  cameraHelp:
    "Décodage sur cet appareil. Aucune image ni aucun fichier n’est envoyé.",
  cameraSecure:
    "La caméra exige HTTPS ou localhost. Utilisez USB ou la saisie manuelle.",
  cameraFailed:
    "Caméra indisponible ou autorisation refusée. Utilisez USB, la saisie manuelle ou une image locale.",
  decodeFailed:
    "Aucun code-barres lisible. Essayez une autre image ou la saisie manuelle.",
  localImage: "Lire une image locale de code-barres",
  invoiceSerialLabel: "Numéro de série :",
  unitHtva: "Unité HTVA",
  totalHtva: "Total HTVA",
  netHtva: "Net HTVA",
  totalTvac: "Total TVAC",
  invoiceSerialMissing: "Non enregistré",
  recordedSerials: "Séries déjà enregistrées (lecture seule)",
};
export const inventoryText = { en, nl, fr };
export function useInventoryText(): InventoryText {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  return inventoryText[lang as keyof typeof inventoryText] || en;
}
